import { useEffect, useMemo, useRef, useState } from 'react';

import { Stack } from '@mui/material';
import { useStore } from '@tanstack/react-form';
import { AxiosError } from 'axios';
import { parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router';
import { z } from 'zod';

import {
  projectFinancialsDefaultValues,
  ProjectFinancialsForm,
  type ProjectFinancialsFormFields,
  projectFinancialsSchema,
} from '../forms/ProjectFinancials';
import type { RegisterProjectExpenseFormData } from '../types';
import { generateProjectExpenseSuccessRedirectUrl } from '../utils';
import {
  useDeleteProjectExpense,
  useGetProjectExpense,
  useUpdateProjectExpense,
} from '@/api/REST/projectExpenses';
import { useGetCurrencyCodeOptionByIsoCode } from '@/api/typesense/currencyCodes';
import { PageTitle } from '@/components/global/PageTitle';
import { Panels } from '@/components/layouts/Panels';
import { DeleteButton } from '@/components/ui/DeleteButton';
import { PageHeader } from '@/components/ui/PageHeader';
import { routes } from '@/constants/routes';
import { useAppForm } from '@/forms/useAppForm';
import { useFormatNumber } from '@/hooks/useFormatNumber';
import { useHasPermissionToAccessPage } from '@/hooks/useHasPermissionToAccessPage';
import { useNotification } from '@/hooks/useNotification';
import { ExpenseSummaryHeader } from '@/pages/register/expenses/components/ExpenseSummaryHeader';
import {
  expenseDetailsDefaultValues,
  ExpenseDetailsForm,
  expenseDetailsSchema,
  type ExpenseDetailsFormFields,
} from '@/pages/register/expenses/forms/ExpenseDetails';
import { ExpenseFileAttachments } from '@/pages/register/expenses/forms/ExpenseFileAttachments';
import {
  projectInformationDefaultValues,
  ProjectInformationForm,
  projectInformationSchema,
  type ProjectInformationFormFields,
} from '@/pages/register/expenses/forms/ProjectInformation';
import { useExpenseFormBlurredValuesStore } from '@/pages/register/expenses/stores/expenseFormBlurredValues';
import {
  calculateExchangeRate,
  generateSpecialCaseErrorMessage,
  getFormIsLockedReasonKey,
} from '@/pages/register/expenses/utils';

export const EditProjectExpense = () => {
  const updateProjectExpense = useUpdateProjectExpense();
  const deleteProjectExpense = useDeleteProjectExpense();
  const successUrlRef = useRef<string>('');
  const { t } = useTranslation('expenses');
  const formRef = useRef<HTMLFormElement>(null);
  const { showError } = useNotification();
  const { id: expenseId } = useParams<{ id: string }>();
  const [isUploadingFiles, setIsUploadingFiles] = useState<boolean>(false);

  const {
    hasPermission: hasProjectAllExpensesPermission,
    isLoading: isProjectAllExpensesPermissionLoading,
  } = useHasPermissionToAccessPage('ProjectAllExpenses');

  const {
    reset: resetBlurredValueStore,
    setExpenseDetailValues,
    setProjectFinancialsValues,
  } = useExpenseFormBlurredValuesStore();

  useEffect(() => {
    resetBlurredValueStore();

    // Cleanup function to reset when component unmounts
    return () => {
      resetBlurredValueStore();
    };
  }, []);

  const {
    data: expenseData,
    isLoading: isLoadingExpense,
    error: expenseDataError,
  } = useGetProjectExpense(Number(expenseId));

  // stabilize parsed date value
  const parsedExpenseDate = useMemo(
    () =>
      !isLoadingExpense && expenseData ? parseISO(expenseData.date!) : null,
    [isLoadingExpense, expenseData],
  );

  const decimalFormattedInitialExpenseAmount = useFormatNumber(
    expenseData?.amount ?? -1,
  );

  const decimalFormattedInitialVatAmount = useFormatNumber(
    expenseData?.vatAmount ?? -1,
  );
  const initialTargetCurrencyConvertedAmount = expenseData
    ? expenseData?.amount * (expenseData?.exchangeRate / 100)
    : -1;

  const decimalFormattedInitialTargetCurrencyConvertedAmount = useFormatNumber(
    initialTargetCurrencyConvertedAmount,
  );

  const decimalFormattedInitialProjectCost = useFormatNumber(
    expenseData?.projectCost ?? -1,
  );
  const decimalFormattedInitialProfitPercentage = useFormatNumber(
    expenseData?.profitPercent ?? -1,
  );
  const decimalFormattedInitialBillableAmount = useFormatNumber(
    expenseData?.salesPrice ?? -1,
  );

  const hasDataLoaded = !isLoadingExpense;

  const isProjectInactive = expenseData?.project?.isActive === false;
  const isContractInactive =
    expenseData?.projectSubContract?.isActive === false;

  const isExpenseLocked =
    hasDataLoaded &&
    (expenseData?.isApproved ||
      expenseData?.isInvoiced ||
      isProjectInactive ||
      isContractInactive);

  // Latch hasDataLoaded: once true it never reverts.
  // This prevents timing issues in refetches reported on background tabs (OIDC auth, TypeSense key rotation) from
  // temporarily flipping hasDataLoaded back to false, which causes defaultValues
  // to revert to empty and formApi.update() to wipe the form fields.
  const hasDataLoadedRef = useRef(false);
  if (hasDataLoaded) hasDataLoadedRef.current = true;
  const stableHasDataLoaded = hasDataLoadedRef.current;

  const form = useAppForm({
    defaultValues: {
      expense_details:
        stableHasDataLoaded && expenseData && parsedExpenseDate
          ? {
              date: parsedExpenseDate,
              supplier: expenseData.supplier ?? null,
              supplierInvoiceNumber: expenseData?.invoiceNo ?? '',
              paymentMethod: expenseData.paymentMethod ?? null,
              amountIncludingVat: decimalFormattedInitialExpenseAmount,
              vatAmount: decimalFormattedInitialVatAmount,
              expenseCurrency: expenseData.currency ?? null,
              expenseType: expenseData.expenseType ?? null,
              targetCurrencyConvertedAmount:
                decimalFormattedInitialTargetCurrencyConvertedAmount,
              expenseComment: expenseData?.comment ?? '',
              expenseNumber: expenseData?.expenseNo ?? '',
              billable: expenseData?.isBillable,
            }
          : expenseDetailsDefaultValues,
      project_information: (stableHasDataLoaded && expenseData
        ? {
            employee: expenseData.employee ?? null,
            customer: expenseData.customer ?? null,
            project: expenseData.project ?? null,
            contract: expenseData.projectSubContract ?? null,
          }
        : projectInformationDefaultValues) satisfies ProjectInformationFormFields,
      project_financials: (stableHasDataLoaded && expenseData
        ? {
            projectCost: decimalFormattedInitialProjectCost ?? '',
            profitPercentage: decimalFormattedInitialProfitPercentage ?? '',
            billableAmount: decimalFormattedInitialBillableAmount ?? '',
          }
        : projectFinancialsDefaultValues) satisfies ProjectFinancialsFormFields,
    },
    validators: {
      onChange: z.object({
        expense_details: expenseDetailsSchema,
        project_information: projectInformationSchema,
        project_financials: projectFinancialsSchema,
      }),
    },
    onSubmit: async ({
      value,
    }: {
      value: {
        expense_details: ExpenseDetailsFormFields;
        project_information: ProjectInformationFormFields;
        project_financials: ProjectFinancialsFormFields;
      };
    }) => {
      const payload = {
        expense_details: value.expense_details,
        project_information: value.project_information,
        project_financials: value.project_financials,
        // when targetCurrencyConvertedAmount is not set, we do not have a diverging currency, so exchange rate should calculate to 100
        exchangeRate: calculateExchangeRate(
          value.expense_details.amountIncludingVat,
          value.expense_details.targetCurrencyConvertedAmount
            ? value.expense_details.targetCurrencyConvertedAmount
            : value.expense_details.amountIncludingVat,
        ),
        legalEntityCurrencyIsoCode: expenseData?.baseReferenceCurrencyCode
          ? expenseData.baseReferenceCurrencyCode
          : '',
      } as RegisterProjectExpenseFormData;

      if (!expenseData?.expenseId) {
        console.error('Expense id to update not loaded successfully');
        return;
      }

      await updateProjectExpense.mutateAsync(
        { id: expenseData?.expenseId, expenseData: payload },
        {
          onSuccess: (response) => {
            successUrlRef.current = generateProjectExpenseSuccessRedirectUrl(
              {
                projectId: response.projectId?.toString(),
                contractId: response.projectSubContractId?.toString(),
                employeeUserId: response.employeeUserId?.toString(),
                paymentMethodId: response.paymentMethodId?.toString(),
                date: response.date,
              },
              {
                projectAccess: response.hasProjectAccess,
                contractAccess: response.hasProjectContractAccess,
                projectAllExpensesTabPageAccess:
                  hasProjectAllExpensesPermission,
              },
              false,
            );
          },
        },
      );
    },
    onSubmitInvalid: () => {
      // this error throwing is important to let SaveFormActions know that the submission failed
      throw new Error('Form is invalid');
    },
  });

  const handleDelete = () => async () => {
    if (!expenseData?.expenseId) {
      console.error('Expense id to delete not loaded successfully');
      return;
    }

    await deleteProjectExpense.mutateAsync(expenseData.expenseId);
  };

  // manually update string and date fields once the data has loaded and init the blurred value store
  useEffect(() => {
    if (
      expenseData?.vatAmount === undefined ||
      expenseData?.amount === undefined ||
      parsedExpenseDate === undefined ||
      !stableHasDataLoaded
    ) {
      return;
    }

    setExpenseDetailValues({
      amountIncludingVat: decimalFormattedInitialExpenseAmount,
      vatAmount: decimalFormattedInitialVatAmount,
      targetCurrencyConvertedAmount:
        decimalFormattedInitialTargetCurrencyConvertedAmount,
    });

    setProjectFinancialsValues({
      projectCost: decimalFormattedInitialProjectCost,
      profitPercentage: decimalFormattedInitialProfitPercentage,
      billableAmount: decimalFormattedInitialBillableAmount,
    });

    form.setFieldValue(
      'expense_details.amountIncludingVat',
      decimalFormattedInitialExpenseAmount,
    );
    //  we reset isTouched manually as the setting in setFieldValue for not updating meta is not working
    form.setFieldMeta('expense_details.amountIncludingVat', (prev) => ({
      ...prev,
      isTouched: false,
    }));

    form.setFieldValue(
      'expense_details.vatAmount',
      decimalFormattedInitialVatAmount,
    );
    form.setFieldMeta('expense_details.vatAmount', (prev) => ({
      ...prev,
      isTouched: false,
    }));

    form.setFieldValue(
      'expense_details.targetCurrencyConvertedAmount',
      decimalFormattedInitialTargetCurrencyConvertedAmount,
    );
    form.setFieldMeta(
      'expense_details.targetCurrencyConvertedAmount',
      (prev) => ({
        ...prev,
        isTouched: false,
      }),
    );

    if (parsedExpenseDate) {
      form.setFieldValue('expense_details.date', parsedExpenseDate);
      form.setFieldMeta('expense_details.date', (prev) => ({
        ...prev,
        isTouched: false,
      }));
    }
  }, [expenseData, parsedExpenseDate, stableHasDataLoaded]);

  // we need this to set the billable checkbox based on the default value of the selected contract
  const selectedContract = useStore(
    form.store,
    (state) => state.values.project_information.contract,
  );

  const previousSelectedContractId = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (selectedContract) {
      if (
        Number(selectedContract?.id) === expenseData?.projectSubContractId &&
        !previousSelectedContractId.current
      ) {
        previousSelectedContractId.current = Number(selectedContract.id);
        // this is the initial billable value of the expense, so we need to make sure billable is set on the expense value, not the contract default
        form.setFieldValue('expense_details.billable', expenseData?.isBillable);
        //  we reset isTouched manually as the setting in setFieldValue for not updating meta is not working
        form.setFieldMeta('expense_details.billable', (prev) => ({
          ...prev,
          isTouched: false,
        }));
      } else if (
        previousSelectedContractId.current === Number(selectedContract.id)
      ) {
        return; // no change in contract selection
      } else {
        form.setFieldValue(
          'expense_details.billable',
          selectedContract.isBillable,
        );
        previousSelectedContractId.current = Number(selectedContract.id);
      }
    }
  }, [selectedContract]);

  const { data: expenseTargetCurrency } = useGetCurrencyCodeOptionByIsoCode(
    expenseData?.baseReferenceCurrencyCode,
  );

  const expenseIsUnchanged = useStore(
    form.store,
    (state) => state.isDefaultValue,
  );

  const cancelNavigationUrl = useMemo(() => {
    const params = {
      projectId: expenseData?.projectId?.toString(),
      contractId: expenseData?.projectSubContractId?.toString(),
      employeeUserId: expenseData?.employeeUserId?.toString(),
      paymentMethodId: expenseData?.paymentMethodId?.toString(),
      date: expenseData?.date,
    };
    return generateProjectExpenseSuccessRedirectUrl(
      params,
      {
        projectAccess: expenseData?.hasProjectAccess ?? false,
        contractAccess: expenseData?.hasProjectContractAccess ?? false,
        projectAllExpensesTabPageAccess: hasProjectAllExpensesPermission,
      },
      false,
    );
  }, [expenseData, hasProjectAllExpensesPermission]);

  if (
    expenseDataError &&
    (expenseDataError as AxiosError).status === 404 &&
    !deleteProjectExpense.isSuccess
  ) {
    showError({
      message: t('edit.ExpenseNotFoundRedirectNotification'),
    });
    return <Navigate to={routes.projects.expenses.new()} replace />;
  }

  return (
    <>
      <PageTitle title={t('editProjectExpense.PageTitle')} />
      <Panels autoSaveId="expense-panels">
        <Panels.Main panelContentProps={{ constrainWidth: true }}>
          <form.Subscribe selector={(state) => state.values}>
            {(val) => (
              <PageHeader
                title={t('editProjectExpense.PageTitle')}
                subtitle={t('editProjectExpense.PageSubtitle')}
              >
                <PageHeader.Info>
                  <ExpenseSummaryHeader
                    expenseCurrency={val.expense_details.expenseCurrency}
                    paymentMethod={val.expense_details.paymentMethod}
                    supplier={val.expense_details.supplier?.value}
                    category={val.expense_details.expenseType?.value}
                    expenseType="project"
                  />
                </PageHeader.Info>
              </PageHeader>
            )}
          </form.Subscribe>
          <form.Form ref={formRef}>
            <Stack gap={{ xs: 2, sm: 4 }}>
              <ProjectInformationForm
                title={t('projectInformation.FormTitle')}
                fields="project_information"
                form={form}
                expenseType="project"
                isParentLoading={!hasDataLoaded}
                formLocked={isExpenseLocked}
                lockedReason={getFormIsLockedReasonKey(
                  isProjectInactive,
                  isContractInactive,
                  !!expenseData?.isApproved,
                  !!expenseData?.isInvoiced,
                )}
              />

              <ExpenseDetailsForm
                title={t('expenseDetails.FormTitle')}
                fields="expense_details"
                form={form}
                expenseType="project"
                expenseTargetCurrency={expenseTargetCurrency ?? null}
                isParentLoading={!hasDataLoaded}
                formLocked={isExpenseLocked}
              />

              <ProjectFinancialsForm
                title={t('projectFinancials.FormTitle')}
                fields="project_financials"
                form={form}
                isParentLoading={!hasDataLoaded}
                isEditPage={true}
                expenseTargetCurrency={expenseTargetCurrency ?? null}
                existingExpenseProjectCurrency={
                  expenseData?.projectCurrency ?? null
                }
                formLocked={isExpenseLocked}
              />

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <DeleteButton
                  disabled={isExpenseLocked || !hasDataLoaded}
                  deleteCallback={handleDelete()}
                  deletionRedirectPath={cancelNavigationUrl}
                />
                <form.SaveFormActions
                  form={form}
                  formRef={formRef}
                  data-automation-id="edit-project-expense-form-actions"
                  cancelNavigateToUrl={cancelNavigationUrl}
                  successNavigationUrl={() => successUrlRef.current}
                  onSubmit={form.handleSubmit}
                  isSubmitDisabled={
                    expenseIsUnchanged ||
                    isExpenseLocked ||
                    isUploadingFiles ||
                    !hasDataLoaded ||
                    isProjectAllExpensesPermissionLoading
                  }
                  getErrorMessageFromError={generateSpecialCaseErrorMessage}
                />
              </Stack>
            </Stack>
          </form.Form>
        </Panels.Main>
        <Panels.Secondary panelContentProps={{ hideOverflow: true }}>
          <ExpenseFileAttachments
            expenseType="project"
            expenseId={expenseData?.expenseId ?? null}
            isExpenseLocked={isExpenseLocked ?? false}
            isExpenseLoading={isLoadingExpense}
            onFilesUploading={setIsUploadingFiles}
          />
        </Panels.Secondary>
      </Panels>
    </>
  );
};
